import React, { useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { LoadingBackDrop, Message, MessageDialog, PrimarySaveConfirmationDialog } from '@lp/lp-ui';
import identityService from '../../services/IdentityService';
import { getBodyErrors } from '../../services/ApiService';
import { InvitationResponseModel, UserListItemModel } from '../../typings';
import { toErrorMessages } from './invitationHelpers';
import InvitationResultPanel from './InvitationResultPanel';

interface ResendInvitationDialogProps {
    // null means closed.
    user: UserListItemModel | null,
    onClose: () => void,
    onResent: () => void
}

const ResendInvitationDialog: React.FC<ResendInvitationDialogProps> = ({ user, onClose, onResent }) => {
    const [busy, setBusy] = useState(false);
    const [messages, setMessages] = useState<Message[]>([]);
    // Bearer credential: component state only, never Redux.
    const [result, setResult] = useState<InvitationResponseModel | null>(null);

    const resend = async (userId: number) => {
        setBusy(true);
        try {
            const response = await identityService.resendInvitation(userId);
            setResult(response.data);
            onResent();
        } catch (err) {
            setMessages(toErrorMessages(await getBodyErrors(err)));
        } finally {
            setBusy(false);
        }
    };

    const handleClose = () => {
        setResult(null);
        onClose();
    };

    return (
        <React.Fragment>
            <PrimarySaveConfirmationDialog
                open={user !== null && result === null}
                title="Resend Invitation"
                contentText={`Send a new link to ${user?.email ?? ''}? The earlier link will no longer work.`}
                actionButtonText="Resend"
                handleSave={() => { if (user) resend(user.id); }}
                handleClose={handleClose}
            />

            {/* The link is shown once; a stray backdrop click must not throw it away. */}
            <Dialog
                open={result !== null}
                onClose={(_event, reason) => { if (reason !== 'backdropClick') handleClose(); }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Resend Invitation</DialogTitle>
                <DialogContent>
                    {result &&
                        <InvitationResultPanel
                            result={result}
                            resending={busy}
                            onResend={() => resend(result.userId)}
                        />
                    }
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Done</Button>
                </DialogActions>
            </Dialog>

            <LoadingBackDrop open={busy} />

            <MessageDialog
                title="Attention"
                messages={messages}
                handleClose={() => setMessages([])}
            />
        </React.Fragment>
    );
};

export default ResendInvitationDialog;
